import { createContext, useCallback, useContext, useReducer, useRef, useState } from "react"
import { AttributeSort } from "@ciq/shared/utils/sort"
import Api from "@ciq/shared/api/api"
import { MAP_BLOCK_PADDING, MAP_INLINE_PADDING } from "@ciq/shared/utils/const"
import { LoadingStatus } from "@ciq/shared/api/loading"
import eventBus from "@ciq/shared/hooks/eventBus"

/**
 * React context for managing global application state including schools, users, loading states, and notifications.
 * Provides access to authentication, school/semester selection, loading indicators, and data management.
 */
export const AppContext = createContext(null)

/**
 * Hook to access the AppContext.
 * Provides access to all global application state and actions.
 *
 * @returns {import('react').Context} The AppContext instance
 */
export const useAppContext = () => useContext(AppContext)

const DEFAULT_PERMISSIONS = {
  users_externally_managed: false,
  can_manage_schools: false,
  can_manage_users: false,
  can_manage_roles: false,
}

const DEFAULT_VIEWS = {
  can_view_map: false,
  can_view_dashboard: false,
  can_view_wifi_config: false,
}

/**
 * @typedef {Object} NotificationSettings - Settings notification counts by category
 * @property {number} count - Total settings notifications
 * @property {number} personal - Personal settings notifications
 * @property {number} users - User management notifications
 * @property {number} roles - Role management notifications
 * @property {number} admin_integrations - Admin integration notifications
 * @property {number} school_integrations - School integration notifications
 */

/**
 * @typedef {Object} NotificationState - Application notification state
 * @property {number} count - Total notification count
 * @property {NotificationSettings} settings - Settings-specific notification counts
 */

/**
 * @typedef {Object} UserState - Current user information
 * @property {string|null} id - User unique identifier
 * @property {string|null} name - User display name
 * @property {string|null} email - User email address
 */

/**
 * Default application context state structure.
 * Defines the initial values for all global application state.
 */
const DEFAULT_APP_CONTEXT = {
  loadingIds: [],
  schools: [],
  school: null,
  semester: null,
  user_settings: {},
  is_system_admin: false,
  notifications: {
    count: 0,
    settings: {
      count: 0,
      personal: 0,
      users: 0,
      roles: 0,
      admin_integrations: 0,
      school_integrations: 0,
    },
  },
  user: {
    id: null,
    name: null,
    email: null,
  },
  permissions: DEFAULT_PERMISSIONS,
  views: DEFAULT_VIEWS,
  show_settings: false,
  landingViewLoadingState: LoadingStatus.NONE,
}

/**
 * Action type constants for the AppReducer.
 * These constants define all possible actions that can be dispatched to modify application state.
 */
const SET_DATA = "SET_DATA"

const ADD_LOADING_ID = "ADD_LOADING_ID"
const REMOVE_LOADING_ID = "REMOVE_LOADING_ID"

const ADD_SCHOOL = "ADD_SCHOOL"
const UPDATE_SCHOOL = "UPDATE_SCHOOL"

const SET_NOTIFICATIONS = "SET_NOTIFICATIONS"

const SET_USER = "SET_USER"
const SET_USER_SETTINGS = "SET_USER_SETTINGS"

const SHOW_SETTINGS = "SHOW_SETTINGS"

const INJECT_DATA = "INJECT_DATA"

const SET_LANDING_VIEW_LOADING_STATE = "SET_LANDING_VIEW_LOADING_STATE"

/**
 * Retrieves the currently selected school from state based on user settings.
 * Returns null if no schools are available or no school_id is set in user settings.
 *
 * @param {Object} state - The current application state
 * @returns {Object|null} The selected school object or null if not found
 */
const getSchool = state => {
  if (!state.schools.length || !state.user_settings.school_id) {
    return null
  }

  return state.schools.find(s => s.id === state.user_settings.school_id)
}

/**
 * Retrieves the currently selected semester from state based on user settings.
 * Returns null if no schools are available, no school/semester is selected, or the school doesn't exist.
 *
 * @param {Object} state - The current application state
 * @returns {Object|null} The selected semester object or null if not found
 */
const getSemester = state => {
  if (!state.schools.length || !state.user_settings.school_id || !state.user_settings.semester_id) {
    return null
  }

  const school = getSchool(state)
  if (!school) return null

  return school.semesters.find(s => s.id === state.user_settings.semester_id)
}

/**
 * Reducer function for managing application state transitions.
 * Handles all state updates including loading states, school/semester selection, user data, and notifications.
 * Emits 'school:changed' events via eventBus when the selected school changes.
 *
 * @param {Object} state - Current application state (defaults to DEFAULT_APP_CONTEXT)
 * @param {Object} action - Action object with type and payload properties
 * @param {string} action.type - The action type constant (SET_DATA, ADD_LOADING_ID, etc.)
 * @param {*} action.payload - The data to update in state (varies by action type)
 * @returns {Object} New application state object
 * @throws {Error} When an unknown action type is dispatched
 */
const AppReducer = (state = DEFAULT_APP_CONTEXT, action) => {
  switch (action.type) {
    case SET_DATA: {
      const newState = {
        ...state,
        ...action.payload,
      }

      const school = getSchool(newState)
      if (school) {
        eventBus.emit("school:changed", school)
        newState.school = school
        // Derive permissions and views from selected school
        newState.permissions = school.permissions || DEFAULT_PERMISSIONS
        newState.views = school.views || DEFAULT_VIEWS
      }

      const semester = getSemester(newState)
      if (semester) {
        newState.semester = semester
      }

      return newState
    }

    case ADD_LOADING_ID:
      return {
        ...state,
        loadingIds: [...state.loadingIds, action.payload],
      }

    case REMOVE_LOADING_ID:
      return {
        ...state,
        loadingIds: state.loadingIds.filter(id => id !== action.payload),
      }

    case ADD_SCHOOL: {
      return {
        ...state,
        school: action.payload,
        schools: [...state.schools, action.payload].sort(AttributeSort("name")),
        permissions: {
          users_externally_managed: false,
          can_manage_schools: true,
          can_manage_users: true,
          can_manage_roles: true,
        },
        views: {
          can_view_map: true,
          can_view_dashboard: true,
          can_view_wifi_config: true,
        },
      }
    }

    case UPDATE_SCHOOL: {
      return {
        ...state,
        schools: [...state.schools.filter(s => s.id !== action.payload.id), action.payload].sort(
          AttributeSort("name")
        ),
      }
    }

    case SET_NOTIFICATIONS: {
      return {
        ...state,
        ...action.payload,
      }
    }

    case SET_USER:
      return {
        ...state,
        user: action.payload,
      }

    case SET_USER_SETTINGS: {
      const newState = {
        ...state,
        user_settings: action.payload,
        loadingIds: state.loadingIds.filter(id => id !== action.loadingId),
      }

      const school = getSchool(newState)
      if (school && state.user_settings.school_id !== school.id) {
        eventBus.emit("school:changed", school)
        newState.school = school
        // Derive permissions and views from newly selected school
        newState.permissions = school.permissions || DEFAULT_PERMISSIONS
        newState.views = school.views || DEFAULT_VIEWS
      }

      const semester = getSemester(newState)
      if (semester) {
        newState.semester = semester
      }

      return newState
    }

    case SHOW_SETTINGS:
      return {
        ...state,
        show_settings: action.payload,
      }

    case INJECT_DATA:
      return {
        ...state,
        [action.key]: {
          ...(state[action.key] || {}),
          ...action.payload,
        },
      }

    case SET_LANDING_VIEW_LOADING_STATE:
      return {
        ...state,
        landingViewLoadingState: action.payload,
      }

    default:
      throw new Error(`Unknown action type ${action.type}`)
  }
}

/**
 * @typedef {Object} AppProviderResult - Complete application context provider result
 * @property {Object|null} mapPadding - Current map padding configuration
 * @property {(padding: Object|null) => void} setMapPadding - Function to update map padding with automatic block padding adjustment
 * @property {boolean} isLoading - Boolean indicating if any loading operations are in progress
 * @property {(key: string) => boolean} isLoadingKey - Function to check if a specific loading key is active
 * @property {boolean} hasData - Boolean indicating if all required data (schools, user, school_id, semester_id) is available
 * @property {(data: Object) => void} setData - Function to update multiple application state properties at once
 * @property {(loadingId: string) => void} addLoadingId - Function to add a loading identifier to track loading state
 * @property {(loadingId: string) => void} removeLoadingId - Function to remove a loading identifier (with 200ms delay)
 * @property {Object|null} school - Currently selected school object
 * @property {string|undefined} schoolId - ID of the currently selected school
 * @property {string|undefined} timezone - Timezone of the currently selected school
 * @property {(school: Object) => void} setSchool - Function to set the current school (triggers API call)
 * @property {(school: Object) => void} addSchool - Function to add a school to the schools array
 * @property {(school: Object) => void} updateSchool - Function to update an existing school in the schools array
 * @property {Array<Object>} schools - Array of all available schools
 * @property {Object|null} semester - Currently selected semester object
 * @property {string|undefined} semesterId - ID of the currently selected semester
 * @property {(semester: Object) => void} setSemester - Function to set the current semester (triggers API call)
 * @property {UserState} user - Current user information
 * @property {NotificationState} notifications - Current notification state
 * @property {(notifications: NotificationState) => void} setNotifications - Function to update notification state
 * @property {boolean} isSystemAdmin - Boolean indicating if the current user is a system administrator
 * @property {(user: UserState) => void} setUser - Function to update user information
 * @property {(key: string) => *} getData - Function to retrieve data for a specific key from the context store
 * @property {(key: string, value: *) => void} injectData - Function to inject/update data for a specific key in the context store
 * @property {import("@ciq/shared/api/loading").LoadingStatus} landingViewLoadingState - Current loading state of the landing view
 * @property {(state: import("@ciq/shared/api/loading").LoadingStatus) => void} setLandingViewLoadingState - Function to update the landing view loading state
 */

/**
 * Application context provider that manages global state including authentication, schools, semesters, loading states, and notifications.
 * Provides a comprehensive set of state management functions and derived values for the entire application.
 * Handles automatic school/semester changes, loading state management, and API integration.
 *
 * @returns {AppProviderResult} Complete application context with state and action functions
 */
export const AppProvider = props => {
  const { children } = props
  const [state, dispatch] = useReducer(AppReducer, DEFAULT_APP_CONTEXT)
  const [mapPadding, setMapPadding] = useState(null)

  const addLoadingId = useCallback(loadingId => {
    console.debug(`Adding '${loadingId}' loading id`)
    dispatch({ type: ADD_LOADING_ID, payload: loadingId })
  }, [])

  const removeLoadingId = useCallback(loadingId => {
    // delay the call a couple of milliseconds to help with flicker
    const handleDispatch = () => {
      console.debug(`Removing '${loadingId}' loading id`)
      dispatch({ type: REMOVE_LOADING_ID, payload: loadingId })
    }
    setTimeout(handleDispatch, 200)
  }, [])

  const value = {
    mapPadding,
    setMapPadding: useCallback(padding => {
      if (padding) {
        padding = { ...padding }
      }

      if (padding?.top != null && padding?.bottom != null) {
        padding.top += MAP_BLOCK_PADDING
        padding.bottom += MAP_BLOCK_PADDING
      }

      if (padding?.left != null && padding?.right != null) {
        padding.left += MAP_INLINE_PADDING
        padding.right += MAP_INLINE_PADDING
      }
      setMapPadding(() => padding)
    }, []),
    isLoading: state.loadingIds.length > 0,
    isLoadingKey: useCallback(
      key => {
        return state.loadingIds.includes(key)
      },
      [state.loadingIds]
    ),
    hasData:
      state.schools?.length > 0 &&
      state.user?.id != null &&
      state.user_settings.school_id != null &&
      state.user_settings.semester_id != null,
    setData: useCallback(data => dispatch({ type: SET_DATA, payload: data }), []),
    addLoadingId,
    removeLoadingId,
    school: state.school,
    schoolId: state.school?.id,
    timezone: state.school?.timezone,
    setSchool: useCallback(
      school => {
        addLoadingId("set_school")
        Api.put("/user", { school_id: school.id })
          .then(response => {
            dispatch({ type: SET_USER_SETTINGS, payload: response.data, loadingId: "set_school" })
          })
          .catch(e => {
            removeLoadingId("set_school")
            if (e.handled) return
            console.error(e)
          })
          .finally(() => {
            emitSchoolEvent(school)
          })
      },
      [addLoadingId, removeLoadingId]
    ),
    addSchool: useCallback(school => {
      dispatch({ type: ADD_SCHOOL, payload: school })
      emitSchoolEvent(school)
    }, []),
    updateSchool: useCallback(school => dispatch({ type: UPDATE_SCHOOL, payload: school }), []),
    schools: state.schools,
    semester: state.semester,
    semesterId: state.semester?.id,
    setSemester: useCallback(
      semester => {
        if (!state.school?.id) {
          return
        }

        addLoadingId("set_semester")
        Api.put("/user", { school_id: state.school.id, semester_id: semester.id })
          .then(response => {
            dispatch({ type: SET_USER_SETTINGS, payload: response.data, loadingId: "set_semester" })
          })
          .catch(e => {
            removeLoadingId("set_semester")
            if (e.handled) return
            console.error(e)
          })
      },
      [addLoadingId, removeLoadingId, state.school?.id]
    ),
    user: state.user,
    notifications: state.notifications,
    setNotifications: useCallback(
      notifications => dispatch({ type: SET_NOTIFICATIONS, payload: notifications }),
      []
    ),
    isSystemAdmin: state.is_system_admin,
    setUser: useCallback(user => dispatch({ type: SET_USER, payload: user }), []),

    showSettings: state.show_settings,
    setShowSettings: useCallback(show => dispatch({ type: SHOW_SETTINGS, payload: show }), []),

    permissions: state.permissions,
    views: state.views,

    getData: useCallback(
      key => {
        return state[key]
      },
      [state]
    ),
    injectData: useCallback((key, value) => {
      dispatch({ type: INJECT_DATA, key, payload: value })
    }, []),

    landingViewLoadingState: state.landingViewLoadingState,
    setLandingViewLoadingState: useCallback(
      state => dispatch({ type: SET_LANDING_VIEW_LOADING_STATE, payload: state }),
      []
    ),
  }

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

/**
 * @typedef {Object} DataStructure - Data structure configuration for context storage
 * @property {string} KEY - The unique key to identify the data in the context store
 * @property {*} DEFAULT_DATA - The default data to use when resetting
 */

/**
 * @typedef {Object} DataInjectorResult - Result object from useDataInjector hook
 * @property {*} data - The current data for the key (or default data if not found)
 * @property {(value: *) => void} setData - Function to update the data for the key
 * @property {() => void} reset - Function to reset the data to the default value
 * @property {() => void} reload - Function to reload the data by resetting loading state and clearing errors
 */

/**
 * Custom hook for managing data in the application context with a specific key.
 * Provides a convenient interface for getting, setting, resetting, and reloading data with memoized callbacks.
 *
 * @param {DataStructure} dataStruct - The data structure configuration object
 * @returns {DataInjectorResult} Object containing data and action functions
 *
 * @example
 * const MapUserData = { KEY: 'user', DEFAULT_DATA: { name: '', email: '' } }
 * const { data: userData, setData: setUserData, reset: resetUserData, reload: reloadUserData } = useDataInjector(MapUserData)
 *
 * // Get current data
 * console.log(userData)
 *
 * // Update data
 * setUserData({ name: 'John', email: 'john@example.com' })
 *
 * // Reset to default
 * resetUserData()
 *
 * // Reload data (reset loading state and clear errors)
 * reloadUserData()
 */
export function useDataInjector(dataStruct) {
  const { getData, injectData } = useAppContext()

  const dataStructRef = useRef(dataStruct)
  const injectDataRef = useRef(injectData)

  const data = getData(dataStruct.KEY) ?? dataStruct.DEFAULT_DATA
  const dataRef = useRef(null)
  if (data !== dataRef.current) {
    dataRef.current = data
  }

  const setData = useCallback(value => {
    let resolved
    if (typeof value === "function") {
      resolved = value(dataRef.current)
    } else {
      resolved = value
    }

    injectDataRef.current(dataStructRef.current.KEY, resolved)
  }, [])

  const reset = useCallback(() => {
    injectDataRef.current(dataStructRef.current.KEY, dataStructRef.current.DEFAULT_DATA)
  }, [])

  const reload = useCallback(() => {
    injectData(dataStruct.KEY, {
      ...data,
      loadingState: LoadingStatus.NONE,
      error: null,
    })
  }, [data, dataStruct.KEY, injectData])

  return { data, setData, reset, reload }
}

const emitSchoolEvent = school => {
  eventBus.emit("school:changed", school)
}
